/**
 * useSimplePortfolioValue Hook
 *
 * Enhanced portfolio value calculator supporting TWO modes:
 * 1. Amount-only: Manual funds (index funds, unit trusts, real estate)
 * 2. Quantity-based: Trackable assets with live pricing (stocks, ETFs)
 *
 * Automatically calculates:
 * - Cost basis (what you invested)
 * - Market value (current worth) - when quantity + price available
 * - Gain/Loss (profit/loss) - when quantity + price available
 */

import { useMemo } from 'react';
import { usePortfolioPrices } from './usePricing';
import { useExchangeRates } from './useExchangeRates';

/**
 * Hook for calculating portfolio value with hybrid tracking
 *
 * Supports both:
 * - Quantity-based: investments with { quantity, purchasePrice } → calculates market value & gain/loss
 * - Amount-only: investments with { amount } → shows cost basis only
 *
 * @param {Array} investments - Array of investment objects
 * @param {string} displayCurrency - Target display currency
 * @returns {Object} Portfolio metrics including market value and gain/loss
 */
export const useSimplePortfolioValue = (investments = [], displayCurrency = 'EUR') => {
  const { prices, loading: pricesLoading, error: pricesError, lastUpdated, refresh } = usePortfolioPrices(investments);
  const { rates: exchangeRates, loading: ratesLoading } = useExchangeRates();

  const loading = pricesLoading || ratesLoading;

  // Calculate portfolio metrics
  const portfolioMetrics = useMemo(() => {
    console.log('📊 [useSimplePortfolioValue] Calculating metrics...');
    console.log('📊 [useSimplePortfolioValue] Investments:', investments?.length || 0);
    console.log('📊 [useSimplePortfolioValue] Prices available:', Object.keys(prices || {}).length);

    if (!investments || investments.length === 0) {
      return {
        totalCostBasis: 0,
        totalMarketValue: 0,
        totalGainLoss: 0,
        totalGainLossPercent: 0,
        investmentCount: 0,
        pricedCount: 0,
        unpricedCount: 0,
        trackableCount: 0,
        manualCount: 0,
        investmentDetails: []
      };
    }

    let totalCostBasis = 0;
    let totalMarketValue = 0;
    let pricedCount = 0;
    let unpricedCount = 0;
    let trackableCount = 0; // Investments with quantity tracking
    let manualCount = 0;     // Investments without quantity tracking
    const investmentDetails = [];

    investments.forEach((investment) => {
      const priceData = prices[investment.ticker];

      console.log(`📊 [useSimplePortfolioValue] Processing ${investment.name}:`, {
        ticker: investment.ticker,
        hasPriceData: !!priceData,
        currentPrice: priceData?.price || null,
        quantity: investment.quantity,
        purchasePrice: investment.purchasePrice || investment.averageCostBasis || investment.pricePerUnit
      });

      const investmentCurrency = investment.currency || 'GBP';
      // Support both field names: purchasePrice (new) and averageCostBasis (existing)
      const purchasePrice = investment.purchasePrice || investment.averageCostBasis || investment.pricePerUnit;
      const hasQuantity = investment.quantity && purchasePrice;

      // Calculate cost basis
      let costBasis;
      if (hasQuantity) {
        // Quantity-based: quantity × purchase price
        // Also check totalCostBasis which may already be calculated
        costBasis = investment.totalCostBasis || (investment.quantity * purchasePrice);
        trackableCount++;
      } else {
        // Amount-only: use amount field
        costBasis = investment.amount || 0;
        manualCount++;
      }

      // Convert cost basis to display currency
      let convertedCostBasis = costBasis;
      if (investmentCurrency !== displayCurrency && exchangeRates?.rates) {
        const fromRate = exchangeRates.rates[investmentCurrency] || 1;
        const toRate = exchangeRates.rates[displayCurrency] || 1;
        convertedCostBasis = (costBasis / fromRate) * toRate;
      }

      totalCostBasis += convertedCostBasis;

      // Calculate market value (if possible)
      let marketValue = convertedCostBasis; // Default to cost basis
      let gainLoss = 0;
      let gainLossPercent = 0;
      let hasCalculatedValue = false;

      if (hasQuantity && priceData && priceData.price) {
        // We have both quantity and current price - calculate market value!
        marketValue = investment.quantity * priceData.price;

        // Convert market value to display currency
        if (investmentCurrency !== displayCurrency && exchangeRates?.rates) {
          const fromRate = exchangeRates.rates[investmentCurrency] || 1;
          const toRate = exchangeRates.rates[displayCurrency] || 1;
          marketValue = (marketValue / fromRate) * toRate;
        }

        gainLoss = marketValue - convertedCostBasis;
        gainLossPercent = convertedCostBasis > 0 ? (gainLoss / convertedCostBasis) * 100 : 0;
        hasCalculatedValue = true;
        pricedCount++;
      } else if (priceData && priceData.price) {
        // Has price but no quantity - mark as priced but can't calculate value
        pricedCount++;
      } else {
        // No price data
        unpricedCount++;
      }

      totalMarketValue += marketValue;

      investmentDetails.push({
        ...investment,
        costBasis: convertedCostBasis,
        marketValue,
        gainLoss,
        gainLossPercent,
        currentPrice: priceData?.price || null,
        hasPrice: !!priceData,
        hasCalculatedValue,
        hasQuantity,
        priceData
      });
    });

    const totalGainLoss = totalMarketValue - totalCostBasis;
    const totalGainLossPercent = totalCostBasis > 0 ? (totalGainLoss / totalCostBasis) * 100 : 0;

    return {
      totalCostBasis,
      totalMarketValue,
      totalGainLoss,
      totalGainLossPercent,
      investmentCount: investments.length,
      pricedCount,
      unpricedCount,
      trackableCount,
      manualCount,
      investmentDetails
    };
  }, [investments, prices, exchangeRates, displayCurrency]);

  return {
    ...portfolioMetrics,
    loading,
    error: pricesError,
    lastUpdated,
    refresh,
    displayCurrency
  };
};

export default useSimplePortfolioValue;
